import React, { useState, useEffect } from 'react';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Home, Mail, MessageSquare, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const ContactPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Récupérer les paramètres de l'URL
  const urlParams = new URLSearchParams(location.search);
  const pageTitle = urlParams.get('page') || 'Page inconnue';
  const formType = urlParams.get('type') || 'suggestion'; // 'suggestion' ou 'support'
  
  // Générer l'objet automatiquement basé sur le titre de la page
  const generateSubject = () => {
    if (formType === 'support') {
      return `Demande d'assistance - ${pageTitle}`;
    } else {
      return `Suggestion d'amélioration - ${pageTitle}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !message.trim()) {
      setErrorMessage('Veuillez remplir tous les champs');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          subject: generateSubject(),
          message,
          page_title: pageTitle
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(data.detail || 'Une erreur est survenue lors de l\'envoi');
      }
    } catch (error) {
      setErrorMessage('Erreur de connexion. Veuillez réessayer.');
    }

    setIsSubmitting(false);
  };

  // Page de confirmation après envoi
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
        {/* Breadcrumb */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumb 
              items={[
                { id: 'contact', label: formType === 'support' ? 'Support' : 'Suggestion' }
              ]}
            />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="shadow-2xl border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-full mb-6">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
              <h1 className="text-3xl font-bold mb-4">Message envoyé !</h1>
              <p className="text-green-100 text-lg">
                {formType === 'support' 
                  ? 'Votre demande d\'assistance a bien été prise en compte, un technicien va vous recontacter.'
                  : 'Merci pour votre suggestion ! Elle a bien été prise en compte, un technicien va vous recontacter.'
                }
              </p>
            </div>
            
            <CardContent className="p-8 text-center">
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">Prochaines étapes</h3>
                  <p className="text-green-700 text-sm">
                    Vous recevrez une réponse à l'adresse <strong>{email}</strong> dans les 24-48 heures ouvrées.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold px-8 py-3 rounded-lg">
                      <Home className="h-5 w-5 mr-2" />
                      Retour à l'accueil
                    </Button>
                  </Link>
                  
                  <Button 
                    onClick={() => navigate(-1)} 
                    variant="outline"
                    className="px-8 py-3"
                  >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Retour à la page précédente
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Formulaire de contact
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { id: 'contact', label: formType === 'support' ? 'Demande d\'assistance' : 'Faire une suggestion' }
            ]}
          />
        </div>
      </div>

      {/* Hero Banner */}
      <section className={`relative overflow-hidden ${
        formType === 'support' 
          ? 'bg-gradient-to-r from-indigo-600 to-purple-600' 
          : 'bg-gradient-to-r from-blue-600 to-cyan-600'
      }`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              {formType === 'support' ? (
                <HelpCircle className="h-12 w-12 text-white" />
              ) : (
                <MessageSquare className="h-12 w-12 text-white" />
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              {formType === 'support' ? 'Demande d\'assistance' : 'Faire une suggestion'}
            </h1>
            <p className="text-xl text-white text-opacity-90 max-w-3xl mx-auto">
              {formType === 'support' 
                ? 'Notre équipe d\'experts est là pour vous accompagner'
                : 'Aidez-nous à améliorer TICLIC.NET avec vos suggestions'
              }
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl text-center">
              {formType === 'support' ? 'Décrivez votre problème' : 'Partagez votre suggestion'}
            </CardTitle>
            <CardDescription className="text-center text-lg">
              Concernant : <strong>{pageTitle}</strong>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Objet (automatique, affiché en lecture seule) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Objet du message
                </label>
                <input
                  type="text"
                  value={generateSubject()}
                  disabled
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Objet généré automatiquement basé sur la page visitée
                </p>
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre adresse email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre.email@entreprise.com"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                />
              </div>
              
              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formType === 'support' ? 'Décrivez votre problème' : 'Votre suggestion'}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    formType === 'support' 
                      ? 'Décrivez en détail le problème rencontré, les étapes que vous avez suivies, et le comportement attendu...'
                      : 'Décrivez votre suggestion d\'amélioration, procédure manquante, ou commentaire sur le contenu...'
                  }
                  required
                  disabled={isSubmitting}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 resize-vertical"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Plus votre description est détaillée, plus nous pourrons vous aider efficacement.
                </p>
              </div>
              
              {/* Error Message */}
              {errorMessage && (
                <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {errorMessage}
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`w-full font-bold py-4 text-lg transition-all duration-200 ${
                  formType === 'support'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                } text-white rounded-lg shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:transform-none`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Envoi en cours...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="h-5 w-5 mr-3" />
                    {formType === 'support' ? 'Envoyer la demande d\'assistance' : 'Envoyer la suggestion'}
                  </div>
                )}
              </Button>
            </form>
            
            {/* Help Text */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-sm text-center">
                <Mail className="h-4 w-4 inline mr-1" />
                Vous recevrez une réponse par email dans les 24-48 heures ouvrées.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default ContactPage;